/**
 * Shared helpers for Support at Home suggestion engine projections and contributions.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { StrategyCandidate } from './SupportAtHomeStrategyApplicator.ts';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../shared/src/calculation/support/capitalisingLoans.ts';
/** One year of a projection (`runProjections` result row). */
export interface SupportAtHomeProjectionYear {
    cashFlow?: number;
    cashflow?: number;
    [key: string]: unknown;
}

/** `computeSupportAtHomeBaselineData` / `calculateIndividualContributions`-shaped contribution bag. */
export interface SupportAtHomeContributionBranch {
    contributionRates?: { clinical?: number; independence?: number; everydayLiving?: number };
    contributionAmounts?: { totalAnnual?: number };
    totalAnnual?: number;
    [key: string]: unknown;
}

export interface SupportAtHomeContributions {
    client?: SupportAtHomeContributionBranch;
    combined?: SupportAtHomeContributionBranch;
    [key: string]: unknown;
}

/** Display-only service-plan cost bag (isEstimated/estimatedDescription plus the modelled $ contribution). */
export interface ServicePlanCosts {
    isEstimated?: boolean;
    estimatedDescription?: string | null;
    contribution?: { total?: number };
    [key: string]: unknown;
}

/** Projections use `cashFlow`; some legacy paths used `cashflow`. */
export function getProjectionCashFlow(projection: SupportAtHomeProjectionYear | null | undefined): number {
    if (!projection) return 0;
    const value = projection.cashFlow ?? projection.cashflow;
    return Number.isFinite(value) ? (value as number) : 0;
}

export function getAnnualContributionAmount(contributions: SupportAtHomeContributions | null | undefined, isPartnered: boolean): number {
    const branch = isPartnered ? contributions?.combined : contributions?.client;
    return branch?.contributionAmounts?.totalAnnual ?? branch?.totalAnnual ?? 0;
}

/** Client out-of-pocket annual contribution from service plan costs (not gross catalogue fees). */
export function getAnnualClientContributionFromServicePlan(servicePlanCosts: ServicePlanCosts | null | undefined): number {
    return servicePlanCosts?.contribution?.total ?? 0;
}

export function buildSupportAtHomeProjectionOptions(
    strategyConfig: StrategyCandidate | null | undefined,
    servicePlanCosts: ServicePlanCosts | null | undefined,
    evaluationStrategyOptions: Record<string, unknown> = {},
    livingExpenses: number | string = 0,
): Record<string, unknown> {
    const integratedAgedCareCost = getAnnualClientContributionFromServicePlan(servicePlanCosts);
    const options: Record<string, unknown> = {
        scenarioLivingExpenses: livingExpenses,
        integratedAgedCareCost,
        ...evaluationStrategyOptions,
    };

    if (strategyConfig?.type === 'reverseMortgage') {
        options.reverseMortgage = true;
        options.reverseMortgageDetails = {
            loanAmount: strategyConfig.loanAmount,
            interestRate: strategyConfig.interestRate ?? REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: strategyConfig.loanStructure || 'lump_sum',
            establishmentFees: strategyConfig.establishmentFees ?? 0,
            useForRad: false,
        };
    }

    if (strategyConfig?.type === 'heas') {
        options.heas = true;
        options.heasDetails = {
            paymentAmount: strategyConfig.paymentAmount,
            paymentFrequency: strategyConfig.paymentFrequency || 'fortnightly',
            interestRate: strategyConfig.interestRate ?? 3.95,
        };
    }

    return options;
}

/** Avoid double-counting RM/HEAS debt when projections track balances via strategyOptions. */
export function prepareFactFindForSupportAtHomeProjections(
    factFind: DraftFactFind,
    strategyConfig: StrategyCandidate | null | undefined,
): DraftFactFind {
    const data: DraftFactFind = structuredClone(factFind);
    if (strategyConfig?.type === 'reverseMortgage' && data.liabilities?.loans) {
        data.liabilities.loans = data.liabilities.loans.filter(
            (l) => l.id !== 'reverse-mortgage' && l.type !== 'reverse_mortgage'
        );
    }
    return data;
}
